import { useState } from 'react';
import { useList } from '@refinedev/core';
import { Link } from 'react-router';
import { DataTable, type Column } from '../../../components/DataTable.js';
import { ReportFilterBar } from '../../../components/ReportFilterBar.js';
import { MiniBarChart } from '../../../components/charts/MiniBarChart.js';

interface LeaseExpirationRow {
  leaseId: string;
  propertyName: string;
  unitLabel: string;
  endDate: string;
  daysUntilExpiration: number;
  primaryTenantName: string | null;
}

const columns: Column<LeaseExpirationRow>[] = [
  { key: 'property', header: 'Property', render: (r) => r.propertyName },
  { key: 'unit', header: 'Unit', render: (r) => r.unitLabel },
  { key: 'tenant', header: 'Tenant', render: (r) => r.primaryTenantName ?? '—' },
  { key: 'end', header: 'End date', render: (r) => r.endDate },
  {
    key: 'days',
    header: 'Days left',
    numeric: true,
    render: (r) => (
      <span className={r.daysUntilExpiration <= 30 ? 'pill pill--late' : 'pill'}>
        {r.daysUntilExpiration}d
      </span>
    ),
  },
  {
    key: 'actions',
    header: '',
    render: (r) => <Link to={`/staff/leases/${r.leaseId}`}>Open lease →</Link>,
  },
];

function byWindow(rows: LeaseExpirationRow[]) {
  const buckets = { '≤30d': 0, '31–60d': 0, '61–90d': 0, '91–120d': 0, '121d+': 0 };
  for (const row of rows) {
    const d = row.daysUntilExpiration;
    if (d <= 30) buckets['≤30d'] += 1;
    else if (d <= 60) buckets['31–60d'] += 1;
    else if (d <= 90) buckets['61–90d'] += 1;
    else if (d <= 120) buckets['91–120d'] += 1;
    else buckets['121d+'] += 1;
  }
  return Object.entries(buckets).map(([label, value]) => ({
    label,
    value,
    displayValue: String(value),
  }));
}

export function LeaseExpirations({
  ownerId,
  onOwnerChange,
  tenantId,
  onTenantChange,
}: {
  ownerId: string;
  onOwnerChange: (value: string) => void;
  tenantId: string;
  onTenantChange: (value: string) => void;
}) {
  const [propertyId, setPropertyId] = useState('');
  const [withinDays, setWithinDays] = useState('120');

  const { data, isLoading, error } = useList<LeaseExpirationRow>({
    resource: 'reports/lease-expirations',
    pagination: { mode: 'off' },
    filters: [
      { field: 'propertyId', operator: 'eq', value: propertyId || undefined },
      { field: 'ownerId', operator: 'eq', value: ownerId || undefined },
      { field: 'tenantId', operator: 'eq', value: tenantId || undefined },
      { field: 'withinDays', operator: 'eq', value: withinDays || undefined },
    ],
  });

  const rows = data?.data ?? [];

  return (
    <div className="stack">
      <p className="muted">
        Active fixed-term leases ending soon. Month-to-month leases have no end date and never
        appear here — feeds renewal outreach.
      </p>

      <ReportFilterBar
        propertyId={propertyId}
        onPropertyChange={setPropertyId}
        ownerId={ownerId}
        onOwnerChange={onOwnerChange}
        tenantId={tenantId}
        onTenantChange={onTenantChange}
        exportPath="/reports/lease-expirations"
        exportParams={{
          propertyId: propertyId || undefined,
          ownerId: ownerId || undefined,
          tenantId: tenantId || undefined,
          withinDays,
        }}
      >
        <label className="row">
          <span className="muted">Within</span>
          <input
            className="field__input"
            type="number"
            min="1"
            style={{ width: '6rem' }}
            value={withinDays}
            onChange={(event) => setWithinDays(event.target.value)}
          />
          <span className="muted">days</span>
        </label>
      </ReportFilterBar>

      {!isLoading && !error && rows.length > 0 ? (
        <div className="card">
          <p className="muted">Expirations by how soon, portfolio-wide.</p>
          <MiniBarChart data={byWindow(rows)} colorVar="--info" severity="descending" />
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.leaseId}
        isLoading={isLoading}
        error={error}
        emptyMessage="Nothing expiring in this window."
      />
    </div>
  );
}
